import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useSite } from "@/context/SiteContext";
import { waLink, generalWaMessage, telLink } from "@/lib/whatsapp";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/poojas", label: "Poojas" },
  { to: "/guidance", label: "Which Pooja?" },
  { to: "/temple-history", label: "Temple" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const site = useSite();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAFAF8]/85 border-b border-border shadow-sm" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img src="/logo.png" alt="Shrigokarnam logo" className="h-12 w-12 object-contain" />
            <span className="leading-tight">
              <span className="block font-heading text-2xl font-semibold text-secondary tracking-tight">Shrigokarnam</span>
              <span className="block text-[10px] tracking-[0.25em] uppercase text-primary font-semibold">Gokarna Temple Poojas</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}-link`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive ? "text-primary" : "text-foreground hover:text-primary"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={waLink(site.whatsapp_number, generalWaMessage)}
              target="_blank"
              rel="nofollow noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-md bg-whatsapp px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            >
              <Phone className="h-4 w-4" /> WhatsApp
            </a>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-secondary" data-testid="mobile-menu-button" aria-label="Open menu">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[#FAFAF8]">
                <div className="mt-8 flex flex-col gap-1">
                  {NAV.map((n) => (
                    <SheetClose asChild key={n.to}>
                      <Link
                        to={n.to}
                        data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}-link`}
                        className={`px-3 py-3 rounded-md text-base font-medium ${
                          location.pathname === n.to ? "bg-muted text-primary" : "text-foreground"
                        }`}
                      >
                        {n.label}
                      </Link>
                    </SheetClose>
                  ))}
                  <a
                    href={telLink(site.phone_number)}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-md border-2 border-gold px-4 py-3 font-semibold text-secondary"
                    data-testid="mobile-call-button"
                  >
                    <Phone className="h-4 w-4" /> Call Us
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
